import {
  AfterViewInit,
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import {WalkTroughService} from '../walkthough.service';
import {Subscription} from 'rxjs';
import {AutoUnsubscribe} from '../../../decorators/auto-unsubscribe.decorator';
import {WalkThroughTooltipComponent} from '../walkthrough-tooltip/walkthrough-tooltip.component';
import {WalkThroughStep, WalkThroughTooltipPosition} from '../../../types/walkthough.type';

@Directive({
  selector: '[walkThroughStep]',
})
@AutoUnsubscribe()
export class WalkThroughStepDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() walkThroughStep;

  private componentRef: ComponentRef<any> = null;
  /* eslint-disable-next-line local/no-subscription-variables */
  private activeStepSubscription: Subscription;
  private initialized: boolean = false;

  constructor(
    private elementRef: ElementRef,
    private appRef: ApplicationRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private walkTroughService: WalkTroughService,
    private render: Renderer2,
  ) {
    this.showTooltip = this.showTooltip.bind(this);
  }

  showTooltip(step: WalkThroughStep) {
    if (this.walkThroughStep !== step?.name) {
      // tooltip for current step is opened, but step has been changed
      if (this.componentRef !== null) {
        this.destroyTooltip();
      }

      return;
    }

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(WalkThroughTooltipComponent);
    this.componentRef = componentFactory.create(this.injector);
    this.appRef.attachView(this.componentRef.hostView);
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    this.setTooltipComponentProperties(step.tooltipPosition);
  }

  ngAfterViewInit() {
    this.initialized = true;

    if (!this.walkThroughStep) {
      return;
    }

    this.activeStepSubscription = this.walkTroughService.activeStepUpdates$.subscribe(this.showTooltip);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      return;
    }

    const {currentValue, previousValue} = changes.walkThroughStep;

    if (previousValue === currentValue) {
      return;
    }

    if (this.activeStepSubscription) {
      this.activeStepSubscription.unsubscribe();
      this.destroyTooltip();
    }

    if (!previousValue && currentValue) {
      this.activeStepSubscription = this.walkTroughService.activeStepUpdates$.subscribe(this.showTooltip);
    }
  }

  destroyTooltip(): void {
    if (this.componentRef !== null) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  ngOnDestroy(): void {
    this.destroyTooltip();
  }

  private setTooltipComponentProperties(position: WalkThroughTooltipPosition) {
    this.componentRef.instance.rect = this.elementRef.nativeElement.getBoundingClientRect();
    this.componentRef.instance.position = position;
  }
}
